import {
  Combobox,
  DropdownButton,
  Icons,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { GetTestManagementIntegrationsRequest } from '@eggplant/api-requests'
import { LinkToTestManagementTool } from '@eggplant/routing'
import { useState } from 'react'
import type {
  ButtonShape,
  OptionType,
  Options,
  TestManagementToolInputs,
  TestManagementTools
} from '@eggplant/types'

interface Props {
  initialOptions?: {
    label: string
    value: string
  }[]
  isRequired?: boolean
  onChange: (value: string, tool: TestManagementTools | '') => void
  onClear?: () => void
  onLoadSuccess?: (data: TestManagementToolInputs[]) => void
  placeholder?: string
  value: string
}

const getOptions = (items: TestManagementToolInputs[]): OptionType[] => items.map(item => ({
  label: item.name,
  value: item.id!
}))

const SelectTestManagementIntegration = ({
  initialOptions = [],
  isRequired = false,
  onChange,
  onClear,
  onLoadSuccess,
  placeholder = 'Select test management tool',
  value
}: Props) => {
  const [items, setItems] = useState<TestManagementToolInputs[]>([])

  const renderButton = (options: Options) => {
    const currentOption = options.find(item => item.value === value)

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const tooltip = currentOption?.value
      ? `${currentOption.label}`
      : ''

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        isHighlighted={!!value}
        label={label}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const handleChange = (selectedValue: string) => {
    const selectedItem = items.find(item => item.id === selectedValue)

    onChange(selectedValue, selectedItem?.tool || '')
  }

  const handleGetSuccess = (data: TestManagementToolInputs[]) => {
    setItems(data)
    onLoadSuccess && onLoadSuccess(data)

    if (value) {
      const selectedItem = data.find(item => item.id === value)

      onChange(value, selectedItem?.tool || '')
    }
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={
        <Icons.TestTools
          aria-hidden
          size='24px'
        />
      }
      linkTo={
        <LinkToTestManagementTool>
          Go to test management tool
        </LinkToTestManagementTool>
      }
      title='No test management tool found'
    />

  return (
    <GetTestManagementIntegrationsRequest onSuccess={handleGetSuccess}>
      {
        () =>
          <Combobox
            initialOptions={initialOptions}
            isRequired={isRequired}
            noOptionsPlaceholder={noDataPlaceholder}
            onChange={handleChange}
            onClear={onClear}
            options={getOptions(items)}
            renderButton={renderButton(getOptions(items))}
            value={value}
          />
      }
    </GetTestManagementIntegrationsRequest>
  )
}

export default SelectTestManagementIntegration
